import React, { useRef, useState, useEffect } from 'react';
import StarRating from '../StarRating';
import arrowIcon from '../../assets/images/user/clarity_arrow-line.png';
import verifiedIcon from '../../assets/images/user/verified.png';
import useLawyerReviews from '../../hooks/useLawyerReviews';
import { fetchReviews } from '../../services/organisationService';
import { generateSolicitorUrl } from '../../utils/urlUtils';

const LawyerCard = ({ lawyer, onClick, isSearchResult = false }) => {
  const { id, name, firm, location, rating, reviews, service, isOnline, photo, isVerified, orgId, google_rating, google_review_count, hasGooglePlacesData, isAiResult, needsReviewFetch } = lawyer;
  const cardRef = useRef(null);
  
  // State for AI result review fetching
  const [aiReviews, setAiReviews] = useState(null);
  const [aiRating, setAiRating] = useState(null);
  const [isLoadingAiReviews, setIsLoadingAiReviews] = useState(false);
  
  // Only skip lazy loading for AI search with fresh Google Places data
  const isAISearchWithGoogleData = hasGooglePlacesData === true;
  
  // OPTIMIZATION: we are not fetching reviews for search results - use only data from search response
  const { reviewsData, loading } = useLawyerReviews(
    (isSearchResult || isAISearchWithGoogleData || (isAiResult && !needsReviewFetch)) ? null : orgId,
    cardRef
  );

  // Generate the URL for the solicitor profile
  const solicitorUrl = generateSolicitorUrl(lawyer);

  // Fetch reviews for AI results when component becomes visible
  useEffect(() => {
    // This effect specifically handles fetching reviews for AI results that need it.
    if (isAiResult && needsReviewFetch && orgId && !aiReviews && !isLoadingAiReviews && cardRef.current && !hasGooglePlacesData) {
      const observer = new IntersectionObserver(
        async (entries) => {
          if (entries[0].isIntersecting) {
            setIsLoadingAiReviews(true);
            try {
              console.log(`Fetching reviews for AI result: ${name} (orgId: ${orgId})`);
              const reviewData = await fetchReviews(orgId, 1); // page 1
              setAiReviews(reviewData);
              
              // Update rating if we got better data
              if (reviewData.stats && reviewData.stats.average_rating) {
                setAiRating(reviewData.stats.average_rating);
              }
            } catch (error) {
              console.error('Failed to fetch reviews for AI result:', error);
            } finally {
              setIsLoadingAiReviews(false);
            }
            observer.disconnect(); // Stop observing after fetching
          }
        },
        { threshold: 0.1 }
      );

      observer.observe(cardRef.current);
      return () => observer.disconnect();
    }
  }, [isAiResult, needsReviewFetch, orgId, aiReviews, isLoadingAiReviews, hasGooglePlacesData]);

  const handleClick = (e) => {
    // If custom onClick handler is provided, prevent default and use it
    if (onClick) {
      e.preventDefault();
      onClick();
      return;
    }
    // Otherwise, let the anchor tag handle navigation naturally
  };

  const getAverageRating = () => {
    // For AI results with fetched reviews, use AI-fetched data
    if (isAiResult && aiRating !== null) {
      return aiRating;
    }
    
    // For AI search with Google Places data, use it directly
    if (isAISearchWithGoogleData && google_rating !== undefined && google_rating !== null) {
      return google_rating;
    }
    
    if (reviewsData && reviewsData.stats && reviewsData.stats.average_rating) {
      return reviewsData.stats.average_rating;
    }
    
    if (reviewsData && reviewsData.reviews && reviewsData.reviews.length > 0) {
      const sum = reviewsData.reviews.reduce((acc, review) => acc + review.rating, 0);
      return (sum / reviewsData.reviews.length).toFixed(1);
    }
    
    // For standard search, prefer existing data structure
    if (google_rating !== undefined && google_rating !== null) {
      return google_rating;
    }
    
    return reviews ? rating || 4 : 4;
  };

  const getReviewsCount = () => {
    // For AI results with fetched reviews, use AI-fetched data
    if (isAiResult && aiReviews && aiReviews.stats) {
      const totalReviews = aiReviews.stats.total_reviews;
      if (totalReviews > 0) {
        return `${totalReviews} ${totalReviews === 1 ? 'review' : 'reviews'}`;
      }
    }
    
    // For AI search with Google Places data, use it directly
    if (isAISearchWithGoogleData && google_review_count !== undefined && google_review_count > 0) {
      return `${google_review_count} ${google_review_count === 1 ? 'review' : 'reviews'}`;
    }
    
    if (reviewsData && reviewsData.stats && reviewsData.stats.total_reviews) {
      return `${reviewsData.stats.total_reviews} ${reviewsData.stats.total_reviews === 1 ? 'review' : 'reviews'}`;
    }
    
    if (reviewsData && reviewsData.reviews && reviewsData.reviews.length > 0) {
      const reviewsArray = reviewsData.reviews;
      const zabootCount = reviewsArray.filter(r => r.source === 'zahoot').length;
      const googleCount = reviewsArray.filter(r => r.source === 'google').length;
      const totalCount = zabootCount + googleCount;
      
      if (totalCount === 0) return null;
      if (googleCount === 0) return `${zabootCount} ${zabootCount === 1 ? 'review' : 'reviews'}`;
      if (zabootCount === 0) return `${googleCount} ${googleCount === 1 ? 'review' : 'reviews'}`;
      return `${totalCount} reviews`;
    }
    
    // For standard search, use google_review_count if available
    if (google_review_count !== undefined && google_review_count > 0) {
      return `${google_review_count} ${google_review_count === 1 ? 'review' : 'reviews'}`;
    }
    
    return reviews ? `${reviews} ${reviews === 1 ? 'review' : 'reviews'}` : null;
  };

  const hasReviews = () => {
    // For AI results with fetched reviews
    if (isAiResult && aiReviews && aiReviews.stats && aiReviews.stats.total_reviews > 0) return true;
    if (isAiResult && aiRating !== null) return true;
    
    // For AI search with Google Places data
    if (isAISearchWithGoogleData) {
      if (google_review_count !== undefined && google_review_count > 0) return true;
      if (google_rating !== undefined && google_rating !== null) return true;
    }
    
    if (reviewsData && reviewsData.stats && reviewsData.stats.total_reviews > 0) return true;
    if (reviewsData && reviewsData.reviews && reviewsData.reviews.length > 0) return true;
    
    // For standard search, check google data as fallback
    if (google_review_count !== undefined && google_review_count > 0) return true;
    if (google_rating !== undefined && google_rating !== null) return true;
    
    return (reviews && reviews > 0);
  };

  return (
    <a 
      ref={cardRef} 
      href={solicitorUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="lawyer-card" 
      id={`lawyer-card-${id}`} 
      onClick={handleClick}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <div className="lawyer-card__photo-container">
        <div className="lawyer-card__photo">
          {!photo && <div className="lawyer-card__photo-placeholder">{name.charAt(0)}</div>}
          {photo && <img src={photo} alt={name} />}
        </div>
      </div>
      
      <h3 className="lawyer-card__name">
        {name}
        {isVerified && (
          <span className="lawyer-card__verified">
            <img src={verifiedIcon} alt="Verified" style={{ width: '16px', height: '16px', verticalAlign: 'middle', marginLeft: '2px' }} />
          </span>
        )}
      </h3>
      
      <div className="lawyer-card__location-badge">
        <span>{location}</span>
      </div>
      
      {(hasReviews() || loading || isLoadingAiReviews) && (
        <div className="lawyer-card__rating">
          {(loading || isLoadingAiReviews) ? (
            <div 
              className="lawyer-card__loading-reviews"
              style={{ 
                background: 'transparent', 
                border: 'none', 
                boxShadow: 'none',
                outline: 'none'
              }}
            >
              <div 
                className="flex items-center space-x-2"
                style={{ 
                  background: 'transparent', 
                  border: 'none',
                  boxShadow: 'none'
                }}
              >
                <div 
                  className="flex space-x-1"
                  style={{ 
                    background: 'transparent', 
                    border: 'none'
                  }}
                >
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="lawyer-card__loading-dot"></div>
                  ))}
                </div>
                <div className="lawyer-card__loading-text"></div>
              </div>
            </div>
          ) : (
            <>
              <StarRating rating={parseFloat(getAverageRating())} />
              {getReviewsCount() && <span className="lawyer-card__reviews">({getReviewsCount()})</span>}
            </>
          )}
        </div>
      )}
      
      <div className="lawyer-card__service-tag">
        <span>{service}</span>
      </div>
    </a>
  );
};

export default LawyerCard; 